import React, { useCallback, useEffect, useState } from 'preact/compat'
import { PageHeader, Separator } from './AccountSectionWrapper'
import { AccountBadge } from './AccountBadge'
import { useToggle } from '../hooks/useToggle'
import { useAccountLayoutContext } from './AccountProfileContext'
import { PageModal } from './AccountModal'
import { FormAccountAddress } from './FormAccountAddress'
import { Suspense } from './Suspense'
import { Address } from '@omneo/omneo-sdk'
import { parseFormDataWithType } from '../helpers/parseFormData'
import { useRefetch } from '../hooks/useRefetch'
import { accountTranslations } from '../helpers/translations'
/**
 *  @typedef {import("../types/test").ShopifyAddress} Address
 */

/**
 * @type {Address[]}
 */

const PrimaryAddressBadge = () => {
  return <AccountBadge color="BLUE" title={accountTranslations.addresses.primary_address} />
}

const VALID_ADDRESS_CHILD_ACTIONS = ['add_address']

const ADDRESS_FORM_KEYS = [
  { key: 'id', type: 'number' },
  { key: 'address_line_1', type: 'string' },
  { key: 'country', type: 'string' },
  { key: 'postcode', type: 'string' },
  { key: 'state', type: 'string' },
  { key: 'city', type: 'string' },
  { key: 'is_default', type: 'bool' }
]

/**
 * @param {Address} props
 */
const AddressRow = (props) => {
  const { isActive, handleToggle } = useToggle(false)
  return (
    <PageModal
      props={{
        isActive,
        handleToggle,
        modalTitle: accountTranslations.addresses.update_address,
        modal: (
          <FormAccountAddress
            address={props}
            handleToggle={handleToggle}
            onSubmitCallback={props.onSubmitCallback}
            handleAddressDeleteCallback={props.handleAddressDeleteCallback}
          />
        )
      }}
    >
      <div className={'flex flex-col gap-2'}>
        <div className={'flex w-full flex-row justify-between'}>
          <div className={'flex flex-row gap-4 perspective-0'}>
            <h4 className={'h4 font-bold'}>
              {props.meta?.first_name} {props.meta?.last_name}
            </h4>
            {props.is_default && <PrimaryAddressBadge />}
          </div>
        </div>
        <div className={'flex flex-col capitalize'}>
          <div>{props.address_line_1}</div>
          <div>
            {props.city} {props.state} {props.postcode}
          </div>
          <div></div>
          {props.country}
        </div>
        <div onClick={handleToggle} className={'btn btn-inline btn-text w-fit underline underline-offset-2'}>
          {accountTranslations.addresses.edit_address}
        </div>
      </div>
    </PageModal>
  )
}

export const AccountAddress = () => {
  const { isActive, handleToggle } = useToggle()
  const { refetch, handleRefetch } = useRefetch()
  const { config, omneoClient } = useAccountLayoutContext()

  /** @type {[Address[], React.Dispatch<React.SetStateAction<Address[]>>]} */
  const [addressData, setAddressData] = useState([])

  useEffect(() => {
    if (config.childPath !== '') {
      const validActionFound = VALID_ADDRESS_CHILD_ACTIONS.find((o) => o === config.childPath)
      if (validActionFound) {
        handleToggle()
      }
    }
  }, [config])

  const getAddressData = async () => {
    const address = await omneoClient.profileClient.getAddresses()
    if (!address?.length) return
    setAddressData([...address])
  }

  const handleAddressDeleteCallback = useCallback(async (id) => {
    await omneoClient.profileClient.deleteAddressById(id)
    setAddressData([])
    handleRefetch()
  }, [])

  const onSubmitCallback = useCallback(async (e) => {
    e.preventDefault()
    const data = new FormData(e.currentTarget)
    const payload = parseFormDataWithType(data, ADDRESS_FORM_KEYS)

    /** If the checkbox value is not check it wont return a false value so this is just quick fix */
    if (!payload?.is_default) {
      payload.is_default = false
    }

    const addressPayload = {
      ...payload,
      meta: {
        first_name: data.get('first_name'),
        last_name: data.get('last_name')
      }
    }

    if (addressPayload?.id) {
      // Edit callback
      await omneoClient.profileClient.updateAddressById(addressPayload.id, addressPayload)
    } else {
      // Create Address
      await omneoClient.profileClient.createAddress(addressPayload)
      handleToggle()
    }

    handleRefetch()
  }, [])

  return (
    <div className={'flex flex-col gap-4'}>
      <PageModal
        props={{
          isActive,
          handleToggle,
          modalTitle: accountTranslations.addresses.add_address,
          modal: <FormAccountAddress onSubmitCallback={onSubmitCallback} handleToggle={handleToggle} />
        }}
      >
        <PageHeader
          title={accountTranslations.addresses.title}
          cta={{
            title: accountTranslations.addresses.add_address,
            hook: 'ADD_ADDRESS',
            callback: handleToggle
          }}
        />
        <Suspense
          refetch={refetch}
          props={{
            asyncCallback: getAddressData
          }}
        >
          {addressData
            .sort((a, b) => b?.is_primary - a?.is_primary)
            .map((address, index) => (
              <>
                <AddressRow
                  key={address.id}
                  {...address}
                  handleAddressDeleteCallback={handleAddressDeleteCallback}
                  onSubmitCallback={onSubmitCallback}
                />
                {index !== addressData.length - 1 && <Separator />}
              </>
            ))}
        </Suspense>
      </PageModal>
    </div>
  )
}
